/**
 * The JSON form of the AST (§4.3): the stored source of truth for a runtime formula. Every node carries `kind`
 * and `span`; the root carries `astVersion`. Written compactly, in a fixed key order, so the C# and TypeScript
 * forms are structurally identical for the same tree and each reads the other's output. They are not
 * byte-identical: a string value is escaped by each language's encoder — C#'s writes DEL, the no-break space,
 * U+2028 and a character outside the BMP as `\uXXXX`, JSON.stringify writes them raw — which changes no value
 * read back.
 */
export const AST_VERSION = 1;
export function toJson(root) {
    return JSON.stringify(serialize(root, true));
}
export function fromJson(json) {
    const root = JSON.parse(json);
    if (root.astVersion !== undefined && root.astVersion !== AST_VERSION) {
        throw new Error(`Formula AST version ${root.astVersion} is not supported; this build reads version ${AST_VERSION}.`);
    }
    return readNode(root);
}
function serialize(node, isRoot) {
    const out = {};
    if (isRoot)
        out.astVersion = AST_VERSION;
    out.kind = node.kind;
    writeFields(node, out);
    out.span = node.span;
    return out;
}
function writeFields(node, out) {
    switch (node.kind) {
        case 'num':
        case 'str':
        case 'bool':
            out.value = node.value;
            break;
        case 'null':
            out.value = null;
            break;
        case 'ref':
            out.symbol = node.symbol;
            out.path = node.path;
            break;
        case 'call':
            writeCall(node, out);
            break;
        default:
            writeCompositeFields(node, out);
    }
}
function writeCompositeFields(node, out) {
    switch (node.kind) {
        case 'unary':
            out.op = node.op;
            out.operand = serialize(node.operand, false);
            break;
        case 'binary':
            out.op = node.op;
            out.left = serialize(node.left, false);
            out.right = serialize(node.right, false);
            break;
        case 'cond':
            out.test = serialize(node.test, false);
            out.then = serialize(node.then, false);
            out.else = serialize(node.else, false);
            break;
        case 'coalesce':
            out.left = serialize(node.left, false);
            out.right = serialize(node.right, false);
            break;
        default:
            throw new Error(`Unknown node kind ${node.kind}.`);
    }
}
function writeCall(node, out) {
    out.fn = node.fn;
    out.args = node.args.map((arg) => serialize(arg, false));
    const named = {};
    for (const n of node.named)
        named[n.name] = serialize(n.value, false);
    out.named = named;
}
function readNode(element) {
    const span = element.span;
    const kind = element.kind;
    switch (kind) {
        case 'num':
            return { kind, value: element.value, span };
        case 'str':
            return { kind, value: element.value, span };
        case 'bool':
            return { kind, value: element.value, span };
        case 'null':
            return { kind, span };
        case 'ref':
            return { kind, symbol: typeof element.symbol === 'string' ? element.symbol : null, path: element.path, span };
        case 'call':
            return readCall(element, span);
        default:
            return readComposite(kind, element, span);
    }
}
function readComposite(kind, element, span) {
    switch (kind) {
        case 'unary':
            return { kind, op: element.op, operand: readNode(element.operand), span };
        case 'binary':
            return {
                kind,
                op: element.op,
                left: readNode(element.left),
                right: readNode(element.right),
                span,
            };
        case 'cond':
            return {
                kind,
                test: readNode(element.test),
                then: readNode(element.then),
                else: readNode(element.else),
                span,
            };
        case 'coalesce':
            return { kind, left: readNode(element.left), right: readNode(element.right), span };
        default:
            throw new Error(`Unknown formula node kind '${kind}'.`);
    }
}
function readCall(element, span) {
    const fn = element.fn;
    const args = element.args.map(readNode);
    const named = Object.entries(element.named ?? {}).map(([name, value]) => ({
        name,
        value: readNode(value),
    }));
    return { kind: 'call', fn, fnSpan: [span[0], span[0] + fn.length], args, named, span };
}
